import { Fragment, useEffect, useState } from 'react';
import { ChevronDown, ChevronRight, Send } from 'lucide-react';
import {
  getWebhookZustellungen, webhookErneutSenden,
  type WebhookStatZeile, type WebhookZustellungAdmin,
} from '../../../lib/apiStatsApi';
import { formatZeitpunkt } from './format';
import { nutzeSortierung, SortierKopf, type Spalte } from './Tabellen';

interface WebhooksTabelleProps {
  zeilen: WebhookStatZeile[];
  gekuerzt: boolean;
  darkMode: boolean;
}

function StatusPlakette({ z, darkMode }: { z: WebhookStatZeile; darkMode: boolean }) {
  if (z.stillgelegt) {
    return <span className="px-1.5 rounded text-xs font-medium bg-red-100 text-red-700" title={`Stillgelegt am ${formatZeitpunkt(z.stillgelegt)} nach wiederholten Fehlschlägen`}>stillgelegt</span>;
  }
  if (!z.aktiv) {
    return <span className={`px-1.5 rounded text-xs font-medium ${darkMode ? 'bg-slate-700 text-slate-300' : 'bg-slate-200 text-slate-600'}`}>pausiert</span>;
  }
  if (z.failCount > 0) {
    return <span className="px-1.5 rounded text-xs font-medium bg-amber-100 text-amber-700" title={`${z.failCount} Fehlschläge in Folge`}>wackelt ({z.failCount})</span>;
  }
  return <span className="px-1.5 rounded text-xs font-medium bg-emerald-100 text-emerald-700">aktiv</span>;
}

/** Zustellprotokoll eines Webhooks — jede Zustellung einzeln erneut sendbar,
 *  auch die erfolgreichen und auch über Mandantengrenzen (Betreiber-Recht). */
function Zustellungen({ webhookId, darkMode }: { webhookId: number; darkMode: boolean }) {
  const [liste, setListe] = useState<WebhookZustellungAdmin[] | null>(null);
  const [fehler, setFehler] = useState(false);
  const [sendetId, setSendetId] = useState<number | null>(null);
  const muted = darkMode ? 'text-slate-400' : 'text-slate-500';

  const laden = () => {
    getWebhookZustellungen(webhookId)
      .then(d => { setListe(d.zustellungen); setFehler(false); })
      .catch(() => setFehler(true));
  };
  useEffect(laden, [webhookId]); // eslint-disable-line react-hooks/exhaustive-deps

  const erneut = async (id: number) => {
    setSendetId(id);
    try {
      await webhookErneutSenden(id);
      laden();
    } catch {
      setFehler(true);
    } finally {
      setSendetId(null);
    }
  };

  if (fehler) return <p className={`px-4 py-3 text-sm ${muted}`}>Protokoll konnte nicht geladen werden.</p>;
  if (!liste) return <p className={`px-4 py-3 text-sm ${muted}`}>Lade …</p>;
  if (liste.length === 0) return <p className={`px-4 py-3 text-sm ${muted}`}>Noch keine Zustellungen.</p>;

  return (
    <ul className={`px-4 py-2 divide-y text-sm ${darkMode ? 'divide-slate-700' : 'divide-slate-200'}`}>
      {liste.map(d => (
        <li key={d.id} className="py-1.5 flex flex-wrap items-center gap-x-3 gap-y-1">
          <span className={`shrink-0 px-1.5 rounded text-xs font-semibold ${
            d.status === 'success' ? 'bg-emerald-100 text-emerald-700'
              : d.status === 'failed' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'
          }`}>
            {d.status === 'success' ? 'ok' : d.status === 'failed' ? 'aufgegeben' : 'offen'}
          </span>
          <span className="font-mono">{d.event}</span>
          <span className={muted}>Versuch {d.attempt}</span>
          <span className={muted}>{d.responseStatus ?? '—'}</span>
          {d.verlauf.length > 1 && (
            <span className={muted} title={d.verlauf.map(v => `Versuch ${v.versuch}: ${v.responseStatus ?? 'kein Kontakt'} — ${v.antwort ?? ''}`).join('\n')}>
              {d.verlauf.length} Versuche
            </span>
          )}
          <span className={`${muted} ml-auto`}>{formatZeitpunkt(d.deliveredAt ?? d.createdAt)}</span>
          <button
            type="button"
            disabled={sendetId === d.id}
            onClick={() => void erneut(d.id)}
            className="inline-flex items-center gap-1 text-blue-600 hover:underline disabled:opacity-50"
          >
            <Send className="w-3 h-3" />
            {sendetId === d.id ? 'sendet …' : 'erneut senden'}
          </button>
        </li>
      ))}
    </ul>
  );
}

export default function WebhooksTabelle({ zeilen, gekuerzt, darkMode }: WebhooksTabelleProps) {
  const [offenId, setOffenId] = useState<number | null>(null);
  const muted = darkMode ? 'text-slate-400' : 'text-slate-500';

  const spalten: Spalte<WebhookStatZeile>[] = [
    { key: 'ziel', titel: 'Webhook', wert: z => z.ziel },
    { key: 'mandant', titel: 'Kunde', wert: z => z.mandant.name },
    { key: 'events', titel: 'Ereignisse', rechts: true, wert: z => z.events.length },
    { key: 'status', titel: 'Status', wert: z => (z.stillgelegt ? 'stillgelegt' : z.aktiv ? 'aktiv' : 'pausiert') },
    { key: 'gesamt', titel: 'Zustellungen', rechts: true, wert: z => z.zustellungen.gesamt },
    { key: 'fehl', titel: 'Fehlgeschlagen', rechts: true, wert: z => z.zustellungen.fehlgeschlagen },
    { key: 'offen', titel: 'Offen', rechts: true, wert: z => z.zustellungen.offen },
    { key: 'letzte', titel: 'Letzte Zustellung', rechts: true, wert: z => z.letzteZustellung, anzeige: z => formatZeitpunkt(z.letzteZustellung) },
  ];

  const { sortierung, sortiere, umschalten } = nutzeSortierung(spalten, 'gesamt');

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[860px] text-sm">
        <SortierKopf spalten={spalten} sortierung={sortierung} umschalten={umschalten} darkMode={darkMode} />
        <tbody className={`divide-y ${darkMode ? 'divide-slate-700' : 'divide-slate-200'}`}>
          {sortiere(zeilen).map(z => (
            <Fragment key={z.id}>
              <tr
                onClick={() => setOffenId(offen => (offen === z.id ? null : z.id))}
                title="Zustellprotokoll anzeigen"
                className={`cursor-pointer ${darkMode ? 'hover:bg-slate-700/40' : 'hover:bg-slate-50'}`}
              >
                <td className="px-4 py-2 font-medium">
                  <span className="inline-flex items-center gap-1.5">
                    {offenId === z.id ? <ChevronDown className="w-3.5 h-3.5 opacity-60" /> : <ChevronRight className="w-3.5 h-3.5 opacity-60" />}
                    <span title={z.url}>{z.ziel}</span>
                  </span>
                  {z.beschreibung && <span className={`ml-2 text-xs ${muted}`}>{z.beschreibung}</span>}
                </td>
                <td className="px-4 py-2">{z.mandant.name}</td>
                <td className="px-4 py-2 text-right" title={z.events.join('\n')}>{z.events.length}</td>
                <td className="px-4 py-2"><StatusPlakette z={z} darkMode={darkMode} /></td>
                <td className="px-4 py-2 text-right">{z.zustellungen.gesamt}</td>
                <td className={`px-4 py-2 text-right ${z.zustellungen.fehlgeschlagen > 0 ? 'text-red-500' : ''}`}>{z.zustellungen.fehlgeschlagen}</td>
                <td className="px-4 py-2 text-right">{z.zustellungen.offen}</td>
                <td className="px-4 py-2 text-right">{formatZeitpunkt(z.letzteZustellung)}</td>
              </tr>
              {offenId === z.id && (
                <tr>
                  <td colSpan={spalten.length} className={darkMode ? 'bg-slate-800/60' : 'bg-slate-50/60'}>
                    <Zustellungen webhookId={z.id} darkMode={darkMode} />
                  </td>
                </tr>
              )}
            </Fragment>
          ))}
          {zeilen.length === 0 && (
            <tr><td colSpan={spalten.length} className={`px-4 py-6 text-center ${muted}`}>Keine Webhooks eingerichtet</td></tr>
          )}
        </tbody>
      </table>
      {gekuerzt && (
        <p className={`px-4 py-2 text-xs ${muted}`}>Auf 500 Zeilen gekürzt — Filter enger stellen.</p>
      )}
    </div>
  );
}
